import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { APP_VERSION } from '../../lib/appVersion';
import { useLocation } from 'react-router-dom';
import { markNavigation, recoverFromChunkError } from '../../lib/chunkRecovery';

// Detects when a newer build has been deployed than the one currently loaded in
// the browser (the classic "users see stale UI after a deploy" problem) and
// prompts a refresh — without anyone having to hard-reload each page manually.
//
// How it works: the build ships /version.json (kept in sync with APP_VERSION).
// We fetch it fresh (no-store + cache-buster) on load, on tab focus, and every
// few minutes. If the deployed version differs from the bundle we're running,
// we show a persistent, non-intrusive toast with a Refresh action. We also
// auto-recover once from a stale-chunk load error after a deploy.
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export default function VersionChecker() {
  const notifiedRef = useRef(false);
  const location = useLocation();

  // A stale chunk may only auto-reload while a route change is under way.
  useEffect(() => { markNavigation(); }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const notifyUpdate = () => {
      if (notifiedRef.current) return;
      notifiedRef.current = true;
      toast('A new version is available', {
        id: 'app-update',
        description: 'Refresh to get the latest updates.',
        duration: Infinity,
        action: {
          label: 'Refresh',
          onClick: () => window.location.reload(),
        },
      });
    };

    const check = async () => {
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data && data.version && data.version !== APP_VERSION) {
          notifyUpdate();
        }
      } catch (_) {
        // Offline, or the SPA fallback served index.html (JSON parse fails) —
        // safe to ignore; we'll try again on the next tick/focus.
      }
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    // Auto-recover from a stale lazy-loaded chunk after a deploy (one reload max
    // per failed load attempt, guarded to avoid an infinite loop). The same recovery
    // runs in ErrorBoundary, which sees the ones React swallows before they reach here.
    const onError = (e) => recoverFromChunkError(e?.message || e?.error?.message);
    const onRejection = (e) => recoverFromChunkError(e?.reason?.message || e?.reason);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
